import { useState } from "react";
import { Send, Loader2 } from "lucide-react";

/**
 * Inline Calculation Form — rendered inside a chat message when the AI
 * emits [[FORM: <id>]]. Each form has a schema (fields + a formatter that
 * builds the string the user "types" into chat on submit).
 *
 * Field types: number, text, select, radio, toggle, month_series
 * Conditional visibility: `visibleWhen: (values) => boolean`
 */

// Utility to safely coerce numeric fields
const num = (v) => (v === "" || v == null ? 0 : Number(v) || 0);

const FORMS = {
  hra: {
    title: "HRA Exemption — enter your figures",
    submitLabel: "Calculate HRA Exemption",
    fields: [
      { name: "basic", label: "Basic Salary (₹ per month)", type: "number", placeholder: "e.g. 50000", required: true },
      { name: "da", label: "DA (₹ per month)", type: "number", placeholder: "e.g. 15000 or 0", required: false },
      { name: "da_retirement", label: "Does DA form part of retirement benefits?", type: "radio", options: ["Yes", "No"], required: false, defaultValue: "No" },
      { name: "commission", label: "Commission — fixed % of turnover (₹ per month)", type: "number", placeholder: "e.g. 0 if none", required: false },
      { name: "hra", label: "HRA received (₹ per month)", type: "number", placeholder: "e.g. 20000", required: true },
      { name: "rent", label: "Actual rent paid (₹ per month)", type: "number", placeholder: "e.g. 18000", required: true },
      { name: "city", label: "City", type: "select", options: [
        "Mumbai", "Delhi", "Kolkata", "Chennai", "Hyderabad", "Pune", "Ahmedabad", "Bengaluru", "Other (non-metro)"
      ], required: true },
    ],
    format: (v) =>
      `Please calculate my HRA exemption with these figures:
- Basic Salary: ₹${v.basic}/month
- DA: ₹${v.da || 0}/month${v.da && Number(v.da) > 0 ? ` (part of retirement benefits: ${v.da_retirement || "No"})` : ""}
- Commission (fixed % of turnover): ₹${v.commission || 0}/month
- HRA received: ₹${v.hra}/month
- Rent paid: ₹${v.rent}/month
- City: ${v.city}

Please show the full computation and taxable HRA.`,
  },

  gratuity: {
    title: "Gratuity Exemption — enter your details",
    submitLabel: "Calculate Gratuity Exemption",
    fields: [
      // ── Step 1: Employer type ────────────────────────────────────────────
      {
        name: "employer_type",
        label: "Are you a Government employee or Non-Government employee?",
        type: "radio",
        options: ["Government", "Non-Government"],
        required: true,
      },

      // ── Government path: just gratuity received (fully exempt anyway) ────
      {
        name: "gov_gratuity_received",
        label: "Actual gratuity received (₹)",
        type: "number",
        placeholder: "e.g. 1500000",
        required: true,
        visibleWhen: (v) => v.employer_type === "Government",
        helper: "Government employees ke liye gratuity fully exempt hoti hai — hum bas amount confirm karenge.",
      },
      {
        name: "gov_at_resignation",
        label: "Is this on retirement / resignation / death / disablement?",
        type: "radio",
        options: ["Yes", "No"],
        required: false,
        defaultValue: "Yes",
        visibleWhen: (v) => v.employer_type === "Government",
      },

      // ── Non-Govt: Coverage question ──────────────────────────────────────
      {
        name: "covered_by_act",
        label: "Are you covered under the Code on Social Security, 2020 (Chapter V — Gratuity)?",
        type: "radio",
        options: ["Yes — Covered", "No — Not Covered"],
        required: true,
        visibleWhen: (v) => v.employer_type === "Non-Government",
        helper: "Coverage applies if your establishment has 10 or more employees on any day in the preceding 12 months. Note: The Payment of Gratuity Act, 1972 has been subsumed into the Code on Social Security, 2020 w.e.f. 21 Nov 2025 — the substantive 15/26 formula remains unchanged.",
      },

      // ── Non-Govt + COVERED path ──────────────────────────────────────────
      {
        name: "cov_last_basic",
        label: "Last drawn Basic Salary (₹ per month)",
        type: "number",
        placeholder: "e.g. 60000",
        required: true,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "Yes — Covered",
      },
      {
        name: "cov_last_da",
        label: "Last drawn DA (₹ per month) — full DA, retirement portion not required",
        type: "number",
        placeholder: "e.g. 15000 or 0",
        required: false,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "Yes — Covered",
      },
      {
        name: "cov_service_years",
        label: "Completed years of service",
        type: "number",
        placeholder: "e.g. 12",
        required: true,
        min: 0,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "Yes — Covered",
      },
      {
        name: "cov_extra_months",
        label: "Additional months (beyond completed years)",
        type: "number",
        placeholder: "e.g. 7",
        required: false,
        min: 0,
        max: 11,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "Yes — Covered",
        helper: "If ≥ 6 months → rounded up to next full year under the Act.",
      },
      {
        name: "cov_gratuity_received",
        label: "Actual gratuity received (₹)",
        type: "number",
        placeholder: "e.g. 700000",
        required: true,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "Yes — Covered",
      },

      // ── Non-Govt + NOT COVERED path ──────────────────────────────────────
      {
        name: "nc_input_mode",
        label: "How do you want to give the last 10 months' salary?",
        type: "toggle",
        options: [
          { value: "average", label: "I'll enter the average (simple)" },
          { value: "monthwise", label: "Enter 10 months separately" },
        ],
        defaultValue: "average",
        required: true,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "No — Not Covered",
        helper: "Rule: Average monthly salary of 10 months preceding the month of retirement/resignation. Salary = Basic + DA (retirement portion) + Commission on fixed % of turnover.",
      },
      {
        name: "nc_include_resign_month",
        label: "Should the month of resignation/retirement itself be included in the 10 months?",
        type: "radio",
        options: ["No — exclude it (standard)", "Yes — include it"],
        defaultValue: "No — exclude it (standard)",
        required: false,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "No — Not Covered",
        helper: "Standard rule: the 10 months immediately preceding the month of retirement (resignation month excluded).",
      },

      // Average-mode fields
      {
        name: "nc_avg_basic",
        label: "Average monthly Basic Salary (₹) — over the 10 months",
        type: "number",
        placeholder: "e.g. 55000",
        required: true,
        visibleWhen: (v) =>
          v.employer_type === "Non-Government" &&
          v.covered_by_act === "No — Not Covered" &&
          v.nc_input_mode === "average",
      },
      {
        name: "nc_avg_da",
        label: "Average monthly DA (retirement portion) (₹) — over the 10 months",
        type: "number",
        placeholder: "e.g. 8000 or 0",
        required: false,
        visibleWhen: (v) =>
          v.employer_type === "Non-Government" &&
          v.covered_by_act === "No — Not Covered" &&
          v.nc_input_mode === "average",
      },
      {
        name: "nc_avg_commission",
        label: "Average monthly Commission (fixed % of turnover) (₹)",
        type: "number",
        placeholder: "e.g. 0 if none",
        required: false,
        visibleWhen: (v) =>
          v.employer_type === "Non-Government" &&
          v.covered_by_act === "No — Not Covered" &&
          v.nc_input_mode === "average",
      },

      // Monthwise-mode: 10 rows
      {
        name: "nc_month_series",
        label: "10 months — Basic / DA (retirement portion) / Commission (₹)",
        type: "month_series",
        rows: 10,
        columns: [
          { key: "basic", label: "Basic", placeholder: "e.g. 55000" },
          { key: "da", label: "DA (ret. portion)", placeholder: "0 if N/A" },
          { key: "comm", label: "Commission", placeholder: "0 if N/A" },
        ],
        required: true,
        visibleWhen: (v) =>
          v.employer_type === "Non-Government" &&
          v.covered_by_act === "No — Not Covered" &&
          v.nc_input_mode === "monthwise",
      },

      // Common tail for non-covered
      {
        name: "nc_service_years",
        label: "Completed years of service (only fully completed — fractions ignored)",
        type: "number",
        placeholder: "e.g. 12",
        required: true,
        min: 0,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "No — Not Covered",
      },
      {
        name: "nc_gratuity_received",
        label: "Actual gratuity received (₹)",
        type: "number",
        placeholder: "e.g. 700000",
        required: true,
        visibleWhen: (v) => v.employer_type === "Non-Government" && v.covered_by_act === "No — Not Covered",
      },
    ],
    format: (v) => {
      const lines = [];
      lines.push(`Please calculate my gratuity exemption with these details:`);
      lines.push(`- Employer type: ${v.employer_type}`);

      if (v.employer_type === "Government") {
        lines.push(`- On retirement/resignation/death/disablement: ${v.gov_at_resignation || "Yes"}`);
        lines.push(`- Gratuity received: ₹${v.gov_gratuity_received}`);
        lines.push(``);
        lines.push(`Please confirm the exemption position and taxable portion (if any).`);
        return lines.join("\n");
      }

      lines.push(`- Covered under Code on Social Security, 2020 (Chapter V — Gratuity) [erstwhile Payment of Gratuity Act, 1972]: ${v.covered_by_act}`);

      if (v.covered_by_act === "Yes — Covered") {
        lines.push(`- Last drawn Basic: ₹${v.cov_last_basic}/month`);
        lines.push(`- Last drawn DA: ₹${v.cov_last_da || 0}/month`);
        const yrs = num(v.cov_service_years);
        const mo = num(v.cov_extra_months);
        lines.push(
          `- Completed service: ${yrs} year${yrs === 1 ? "" : "s"}${mo > 0 ? ` and ${mo} month${mo === 1 ? "" : "s"}` : ""}`,
        );
        lines.push(`- Actual gratuity received: ₹${v.cov_gratuity_received}`);
      } else {
        lines.push(
          `- Resignation month included in 10 months: ${v.nc_include_resign_month || "No — exclude it (standard)"}`,
        );
        if (v.nc_input_mode === "monthwise") {
          lines.push(`- Last 10 months (Basic / DA-ret / Commission):`);
          const rows = Array.isArray(v.nc_month_series) ? v.nc_month_series : [];
          rows.forEach((row, i) => {
            lines.push(
              `   Month ${i + 1}: Basic ₹${num(row?.basic)}, DA ₹${num(row?.da)}, Commission ₹${num(row?.comm)}`,
            );
          });
          const totBasic = rows.reduce((s, r) => s + num(r?.basic), 0);
          const totDA = rows.reduce((s, r) => s + num(r?.da), 0);
          const totComm = rows.reduce((s, r) => s + num(r?.comm), 0);
          const n = rows.length || 10;
          lines.push(
            `   (Averages → Basic ₹${(totBasic / n).toFixed(2)}, DA ₹${(totDA / n).toFixed(2)}, Commission ₹${(totComm / n).toFixed(2)})`,
          );
        } else {
          lines.push(`- Average monthly Basic: ₹${v.nc_avg_basic}`);
          lines.push(`- Average monthly DA (retirement portion): ₹${v.nc_avg_da || 0}`);
          lines.push(`- Average monthly Commission (fixed % of turnover): ₹${v.nc_avg_commission || 0}`);
        }
        lines.push(`- Completed years of service (fractions ignored): ${v.nc_service_years}`);
        lines.push(`- Actual gratuity received: ₹${v.nc_gratuity_received}`);
      }

      lines.push(``);
      lines.push(`Please show the full computation (three limits) and the taxable portion.`);
      return lines.join("\n");
    },
  },

  motor_car: {
    title: "Motor Car Perquisite — enter your details",
    submitLabel: "Calculate Motor Car Perquisite",
    fields: [
      { name: "ownership", label: "Who owns / hires the car?", type: "select", options: ["Employer owns or hires", "Employee owns"], required: true },
      { name: "expenses", label: "Who meets running & maintenance expenses?", type: "select", options: ["Employer", "Employee"], required: true },
      { name: "usage", label: "Usage pattern", type: "select", options: ["Wholly official", "Partly official + partly personal", "Wholly personal"], required: true },
      { name: "engine", label: "Engine capacity", type: "select", options: ["Up to 1600cc (or EV)", "Above 1600cc"], required: true },
      { name: "chauffeur", label: "Chauffeur provided by employer?", type: "radio", options: ["Yes", "No"], required: true, defaultValue: "No" },
      { name: "months_used", label: "Number of months car was used in this tax year", type: "number", placeholder: "e.g. 12", required: true, min: 0, max: 12 },
      { name: "car_cost", label: "Actual cost of car (₹) — only if wholly personal use", type: "number", placeholder: "e.g. 800000 or 0", required: false },
      { name: "employee_recovery", label: "Amount recovered from employee (₹ per month, if any)", type: "number", placeholder: "e.g. 0", required: false },
    ],
    format: (v) =>
      `Please calculate my motor car perquisite with these details:
- Ownership: ${v.ownership}
- Expenses met by: ${v.expenses}
- Usage: ${v.usage}
- Engine capacity: ${v.engine}
- Chauffeur provided by employer: ${v.chauffeur}
- Months used in tax year: ${v.months_used}
${v.car_cost ? `- Actual cost of car: ₹${v.car_cost}` : ""}
${v.employee_recovery ? `- Amount recovered from employee: ₹${v.employee_recovery}/month` : ""}

Please identify the correct scenario and show the taxable perquisite.`,
  },
};

// Build initial default values (including special structures)
const buildInitialValues = (schema) => {
  const initial = {};
  (schema?.fields || []).forEach((f) => {
    if (f.type === "month_series") {
      initial[f.name] = Array.from({ length: f.rows || 10 }, () => ({}));
    } else {
      initial[f.name] = f.defaultValue ?? "";
    }
  });
  return initial;
};

export default function InlineCalcForm({ formId, onSubmit, disabled }) {
  const schema = FORMS[formId?.toLowerCase()];
  const [values, setValues] = useState(() => buildInitialValues(schema));
  const [submitted, setSubmitted] = useState(false);

  if (!schema) {
    return (
      <div className="mt-3 text-xs text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-lg px-3 py-2">
        ⚠️ Unknown form: <code>{formId}</code>
      </div>
    );
  }

  const setField = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  // Which fields are currently visible given the state?
  const visibleFields = schema.fields.filter((f) =>
    typeof f.visibleWhen === "function" ? f.visibleWhen(values) : true,
  );

  // Required-check only over visible fields
  const isFilled = (f) => {
    const val = values[f.name];
    if (f.type === "month_series") {
      const rows = Array.isArray(val) ? val : [];
      // At least the "basic" column filled in for each row
      return rows.length === (f.rows || 10) &&
        rows.every((r) => r && String(r[f.columns[0].key] ?? "").trim() !== "");
    }
    return String(val ?? "").trim() !== "";
  };
  const missingRequired = visibleFields.filter((f) => f.required && !isFilled(f));
  const canSubmit = missingRequired.length === 0 && !submitted && !disabled;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const message = schema.format(values).trim();
    setSubmitted(true);
    onSubmit?.(message);
  };

  const renderField = (f) => {
    const val = values[f.name];

    if (f.type === "select") {
      return (
        <select
          value={val || ""}
          onChange={(e) => setField(f.name, e.target.value)}
          disabled={submitted || disabled}
          className="w-full bg-slate-950/70 border border-slate-700 text-white text-[13px] rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 disabled:opacity-50"
          data-testid={`form-field-${f.name}`}
        >
          <option value="">Select…</option>
          {f.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (f.type === "radio") {
      return (
        <div className="flex flex-wrap gap-2">
          {f.options.map((opt) => {
            const active = val === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setField(f.name, opt)}
                disabled={submitted || disabled}
                className={`flex-1 min-w-[120px] text-[13px] py-2 px-3 rounded-lg border transition-all disabled:opacity-50 ${
                  active
                    ? "border-cyan-400 bg-cyan-500/20 text-white font-semibold"
                    : "border-slate-700 bg-slate-950/40 text-slate-300 hover:border-slate-500"
                }`}
                data-testid={`form-radio-${f.name}-${String(opt).toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    }

    if (f.type === "toggle") {
      // Pill-style two-option toggle
      return (
        <div className="inline-flex rounded-full bg-slate-950/60 border border-slate-700 p-1 gap-1">
          {f.options.map((opt) => {
            const active = val === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setField(f.name, opt.value)}
                disabled={submitted || disabled}
                className={`text-[12.5px] py-1.5 px-3 rounded-full transition-all disabled:opacity-50 ${
                  active
                    ? "bg-cyan-500 text-white font-semibold shadow shadow-cyan-500/30"
                    : "text-slate-300 hover:text-white"
                }`}
                data-testid={`form-toggle-${f.name}-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    }

    if (f.type === "month_series") {
      const rows = Array.isArray(val) ? val : [];
      const setRow = (i, key, v) => {
        const next = rows.slice();
        next[i] = { ...(next[i] || {}), [key]: v };
        setField(f.name, next);
      };
      return (
        <div className="border border-slate-700 rounded-lg overflow-hidden">
          <div
            className="grid gap-0 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-2 py-1.5"
            style={{ gridTemplateColumns: `40px repeat(${f.columns.length}, minmax(0, 1fr))` }}
          >
            <div className="pl-1">#</div>
            {f.columns.map((c) => (
              <div key={c.key} className="pl-1">{c.label}</div>
            ))}
          </div>
          <div className="divide-y divide-slate-800">
            {Array.from({ length: f.rows || 10 }).map((_, i) => (
              <div
                key={i}
                className="grid gap-0 items-center px-2 py-1.5 bg-slate-950/40"
                style={{ gridTemplateColumns: `40px repeat(${f.columns.length}, minmax(0, 1fr))` }}
              >
                <div className="text-[11.5px] text-slate-500 pl-1">M{i + 1}</div>
                {f.columns.map((c) => (
                  <div key={c.key} className="pr-1">
                    <input
                      type="number"
                      value={rows[i]?.[c.key] ?? ""}
                      onChange={(e) => setRow(i, c.key, e.target.value)}
                      placeholder={c.placeholder}
                      disabled={submitted || disabled}
                      className="w-full bg-slate-900/60 border border-slate-800 text-white text-[12.5px] rounded px-2 py-1 focus:outline-none focus:border-cyan-500 disabled:opacity-50"
                      data-testid={`form-month-${f.name}-${i}-${c.key}`}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // default: number / text
    return (
      <input
        type={f.type}
        value={val || ""}
        onChange={(e) => setField(f.name, e.target.value)}
        placeholder={f.placeholder}
        min={f.min}
        max={f.max}
        disabled={submitted || disabled}
        className="w-full bg-slate-950/70 border border-slate-700 text-white text-[13px] rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 disabled:opacity-50"
        data-testid={`form-field-${f.name}`}
      />
    );
  };

  return (
    <div
      className="mt-4 rounded-2xl border border-cyan-500/30 bg-slate-900/60 p-4 backdrop-blur-sm"
      data-testid={`inline-form-${formId}`}
    >
      <div className="text-[11px] uppercase tracking-widest text-cyan-300/90 font-bold mb-3">
        {schema.title}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-4">
        {visibleFields.map((f) => {
          // full width for radio / toggle / month_series / helper-only fields
          const fullWidth =
            f.type === "radio" ||
            f.type === "toggle" ||
            f.type === "month_series";
          // For half-width numeric/text/select fields we use a flex column with
          // a fixed 2-line label slot so left/right inputs always line up on
          // the same baseline even when one label wraps to 2 lines.
          const useAlignedLayout = !fullWidth;
          return (
            <div
              key={f.name}
              className={`${fullWidth ? "sm:col-span-2" : ""} ${useAlignedLayout ? "flex flex-col" : ""}`}
            >
              <label
                className={`block text-[11.5px] text-slate-300 font-medium mb-1 ${
                  useAlignedLayout ? "min-h-[2.6rem] flex items-end leading-tight" : ""
                }`}
              >
                <span>
                  {f.label}
                  {f.required && <span className="text-cyan-400 ml-0.5">*</span>}
                </span>
              </label>
              {renderField(f)}
              {f.helper && (
                <p className="mt-1 text-[10.5px] text-slate-400/80 italic">{f.helper}</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500">
          {submitted
            ? "Sent — calculating…"
            : missingRequired.length
            ? `Fill in the ${missingRequired.length} required field${missingRequired.length > 1 ? "s" : ""}`
            : "Ready to calculate"}
        </p>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-full px-4 py-2 transition-all ${
            canSubmit
              ? "bg-cyan-500 hover:bg-cyan-400 text-white shadow-lg shadow-cyan-500/30"
              : "bg-slate-800 text-slate-500 cursor-not-allowed"
          }`}
          data-testid={`form-submit-${formId}`}
        >
          {submitted ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {schema.submitLabel}
        </button>
      </div>
    </div>
  );
}
